/* Card Holder ID edition is only present in dev and staging */

import { Box, Button, CircularProgress, TextField } from '@mui/material';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { currency, enqueueSnackbarError } from 'lib/helpers';
import { addUserBalance, fetchUserBalance, resetUserBalance, UserBalance as UserBalanceType } from 'lib/models/wallet';

import { useEffect, useState } from 'react';

type Props = {
	onClose: () => void;
	username: string;
};

function UserBalance({ onClose, username }: Props) {
	const [loading, setLoading] = useState(true);

	const [showConfirmation, setShowConfirmation] = useState(false);

	const [userBalance, setUserBalance] = useState<UserBalanceType>({
		balance: 0,
		username,
		points: 0,
	});

	const [value, setValue] = useState(0);

	const onSave = async () => {
		try {
			setLoading(true);
			const result = await addUserBalance(username, value);
			if (result.success) {
				setUserBalance(result.balance);
			} else {
				enqueueSnackbarError('No se pudo cargar el saldo de cortesía');
			}
		} catch (error) {
			console.warn(error);
		} finally {
			setLoading(false);
			setValue(0);
		}
	};

	const onReset = async (confirmed: boolean) => {
		setShowConfirmation(false);
		if (confirmed) {
			try {
				setLoading(true);
				const result = await resetUserBalance(username);
				if (result.success) {
					setUserBalance((current) => ({ ...current, balance: 0 }));
				} else {
					enqueueSnackbarError('No se pudo resetear el saldo de cortesía');
				}
			} catch (error) {
				console.warn(error);
			} finally {
				setLoading(false);
				setValue(0);
			}
		}
	};

	useEffect(() => {
		const search = async () => {
			try {
				setLoading(true);
				const response = await fetchUserBalance(username);
				setUserBalance(response);
			} catch (error) {
				console.warn(error);
			} finally {
				setLoading(false);
			}
		};
		search();
	}, [username]);

	return (
		<Box
			sx={{
				padding: 2,
				gap: 2,
				backgroundColor: 'white',
				borderRadius: 5,
				borderColor: 'gray',
				borderWidth: 1,
				borderStyle: 'dotted',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Box sx={{ mb: 2, color: 'red' }}>
				Usuario {username} con {currency(userBalance.balance)} de saldo de cortesía
			</Box>

			<TextField
				value={value}
				label={'Agregar saldo de cortesía'}
				onChange={(ev) => setValue(Number(ev.target.value))}
				disabled={loading}
				inputMode="numeric"
				inputProps={{
					inputMode: 'numeric',
					step: 1000,
				}}
			/>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
				}}
			>
				{loading && <CircularProgress />}

				<Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
					<Button variant="contained" color="primary" onClick={onSave} disabled={!value}>
						Agregar
					</Button>
					<Button variant="outlined" color="warning" onClick={() => setShowConfirmation(true)}>
						RESETEAR
					</Button>
					<Button variant="contained" color="error" onClick={onClose}>
						Cerrar
					</Button>
				</Box>
			</Box>

			{showConfirmation && (
				<ConfirmationDialog
					title="Confirmar"
					description={`Vas a resetear el saldo de cortesía de ${username}`}
					onClose={onReset}
				/>
			)}
		</Box>
	);
}

export default UserBalance;
